import { faRotate } from '@fortawesome/pro-solid-svg-icons/faRotate';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { Card } from 'ui-components/components/card';
import { Checkbox } from 'ui-components/components/checkbox';
import { Divider } from 'ui-components/components/divider';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { NotificationService, NOTIFICATION_TYPE_ERROR, NOTIFICATION_TYPE_SUCCESS } from 'ui-components/services/notification-service';

import HelpArticleLink from '../../../../../../components/help-article-link';
import { linkIDs } from '../../../../../../services/link-service/links';
import { refreshProducts } from '../../../../../../store/seller/seller.api';
import { useStoreUpdateContext } from '../../context';
import type { StoreSetting } from '../extended-products-tab';
import { messages } from '../messages';
import style from './product-synchronization-card.module.less';

const ENABLE_24_HOUR_PRODUCTS_SYNC = 'Enable24HourProductsSync';
const DEFAULT_24_HOUR_SYNC_VALUE = false;

type ProductSynchronizationCardProps = {
  storeGuid: string;
  storeSettings: StoreSetting[];
};

export const ProductSynchronizationCard = ({ storeGuid, storeSettings = [] }: ProductSynchronizationCardProps) => {
  const { updateTrigger } = useStoreUpdateContext();
  const [isTriggeringSync, setIsTriggeringSync] = useState(false);

  const enable24HourProductsSyncSetting = storeSettings.find((setting) => setting.name === ENABLE_24_HOUR_PRODUCTS_SYNC);
  const isEnable24HourProductsSync = enable24HourProductsSyncSetting
    ? enable24HourProductsSyncSetting.value === 'true'
    : DEFAULT_24_HOUR_SYNC_VALUE;

  const updateProductSync = (value: boolean) => updateTrigger(ENABLE_24_HOUR_PRODUCTS_SYNC, value);

  const handleSyncNow = async () => {
    if (!storeGuid || isTriggeringSync) {
      return;
    }

    setIsTriggeringSync(true);

    try {
      await refreshProducts(storeGuid, true);
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_SUCCESS,
        title: <FormattedMessage {...messages.syncSuccessTitle} />,
        content: <FormattedMessage {...messages.syncSuccessContent} />,
      });
    } catch {
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_ERROR,
        title: <FormattedMessage {...messages.syncErrorTitle} />,
        content: <FormattedMessage {...messages.syncErrorContent} />,
      });
    } finally {
      setIsTriggeringSync(false);
    }
  };

  return (
    <Card className={style['products-card']}>
      <FlexRow className={style['card-title']} gap={Gap.Space8}>
        <Text textType={TextType.H4} value={messages.productSynchronization} />
      </FlexRow>
      <div className={style['card-description']}>
        <Text textType={TextType.Caption} value={messages.productSynchronizationDescription} />
      </div>

      <FlexRow className={style['checkbox-setting']} gap={Gap.Space8}>
        <Checkbox checked={isEnable24HourProductsSync} className={style.checkbox} onChange={(checked) => updateProductSync(checked)} />
        <FlexColumn gap={Gap.Space4}>
          <div className={style['checkbox-label']}>
            <Text textType={TextType.Label} value={messages.synchronizeProductsFromConnectedStores} />
          </div>
          <div className={style['checkbox-description']}>
            <FormattedMessage
              {...messages.synchronizeProductsDescription}
              values={{
                learnMoreLink: (chunks) => <HelpArticleLink articleId={linkIDs.ProductSyncLearnMore}>{chunks}</HelpArticleLink>,
              }}
            />
          </div>
        </FlexColumn>
      </FlexRow>

      <Divider className={style.divider} />

      <div className={style['sync-button-container']}>
        <Button disabled={isTriggeringSync} isLoading={isTriggeringSync} onClick={handleSyncNow} theme={THEME_CONSTANTS.tertiary}>
          <FlexRow className={style['sync-button-content']} gap={Gap.Space8}>
            <FontAwesomeIcon className={style['sync-icon']} icon={faRotate} />
            <Text textType={TextType.Paragraph} value={messages.syncNow} />
          </FlexRow>
        </Button>
      </div>
    </Card>
  );
};
